/**
 * Per-user camera grant tracking.
 *
 * Browser/OS still owns the real MediaDevices permission; we remember that this
 * visitor already completed our one-time in-app consent so we do not show the
 * gate again. After a permanent OS grant, getUserMedia is usually silent.
 * Server also stores camera_granted_at so the flag survives device/storage wipe.
 */

import { storage, KEYS } from "./storage";
import { isApiEnabled } from "../api/config";
import { visitorAuthStorage } from "../api/authStorage";

type GrantMap = Record<string, true>;

function readMap(): GrantMap {
  const legacy = storage.get<boolean>(KEYS.CAMERA_GRANTED, false);
  const map = storage.get<GrantMap>(KEYS.CAMERA_GRANTED_USERS, {});
  if (legacy && Object.keys(map).length === 0) {
    return { "*": true };
  }
  return map && typeof map === "object" ? map : {};
}

function writeMap(map: GrantMap): void {
  storage.set(KEYS.CAMERA_GRANTED_USERS, map);
  storage.set(KEYS.CAMERA_GRANTED, Object.keys(map).length > 0);
}

export function isCameraGrantedForUser(userId: string | null | undefined): boolean {
  const map = readMap();
  if (map["*"]) return true;
  if (!userId) return Boolean(storage.get(KEYS.CAMERA_GRANTED, false));
  return Boolean(map[userId]) || Boolean(map["*"]);
}

/** Sync server flag → local storage (call after /auth/me or login). */
export function hydrateCameraGrantFromServer(
  userId: string | null | undefined,
  cameraGranted: boolean | undefined,
): void {
  if (!userId || !cameraGranted) return;
  const map = readMap();
  map[userId] = true;
  map["*"] = true;
  writeMap(map);
}

/** Fire-and-forget persist to API (does not block camera start). */
export function persistCameraConsentToServer(userId: string | null | undefined): void {
  if (!userId || !isApiEnabled() || !visitorAuthStorage.isAuthenticated()) return;
  void import("../api/auth")
    .then(({ visitorAuthApi }) => visitorAuthApi.saveCameraConsent())
    .catch(() => undefined);
}

export function markCameraGrantedForUser(userId: string | null | undefined): void {
  const map = readMap();
  if (userId) {
    map[userId] = true;
    // Keep device-wide flag so login (pre-auth) also auto-starts next time.
    map["*"] = true;
  } else {
    map["*"] = true;
  }
  writeMap(map);
  persistCameraConsentToServer(userId);
}

/** After badge login: keep the device grant and attach it to the visitor id. */
export function bindCameraGrantToUser(userId: string | null | undefined): void {
  if (!userId) return;
  const map = readMap();
  if (map["*"] || map[userId] || storage.get(KEYS.CAMERA_GRANTED, false)) {
    map[userId] = true;
    map["*"] = true;
    writeMap(map);
    persistCameraConsentToServer(userId);
  } else {
    markCameraGrantedForUser(userId);
  }
}

export type BrowserCameraPermission = "granted" | "denied" | "prompt" | "unknown";

/**
 * Best-effort Permissions API.
 * Some Android/Chrome builds hang forever on permissions.query({name:"camera"}) —
 * never block the allow-button UI waiting for it.
 */
export async function queryBrowserCameraPermission(): Promise<BrowserCameraPermission> {
  try {
    const perms = navigator.permissions;
    if (!perms?.query) return "unknown";
    const status = await Promise.race([
      perms.query({ name: "camera" as PermissionName }),
      new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), 400);
      }),
    ]);
    if (!status) return "unknown";
    if (status.state === "granted" || status.state === "denied" || status.state === "prompt") {
      return status.state;
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Auto-open camera when OS already granted.
 * iOS Permissions API often stays "prompt"/"unknown" forever — never auto-call
 * getUserMedia without a tap (hangs on "Подключение…" and can re-prompt).
 * After the user granted once, a single tap starts instantly with no OS dialog.
 */
export async function shouldAutoStartCamera(
  userId: string | null | undefined,
): Promise<boolean> {
  const browser = await queryBrowserCameraPermission();
  if (browser === "denied") return false;
  if (browser === "granted") return true;
  const ios = typeof navigator !== "undefined" && /iP(hone|od|ad)/.test(navigator.userAgent);
  if (ios) return false;
  return isCameraGrantedForUser(userId);
}

export function clearCameraGrantedForUser(userId: string | null | undefined): void {
  const map = readMap();
  if (userId) delete map[userId];
  delete map["*"];
  writeMap(map);
}

/** Snapshot used to survive logout / storage.clear(). */
export function snapshotCameraGrants(): {
  granted: boolean;
  users: GrantMap;
} {
  return {
    granted: Boolean(storage.get(KEYS.CAMERA_GRANTED, false)),
    users: readMap(),
  };
}

export function restoreCameraGrants(snap: { granted: boolean; users: GrantMap }): void {
  if (snap.users && Object.keys(snap.users).length > 0) {
    writeMap(snap.users);
    return;
  }
  if (snap.granted) {
    writeMap({ "*": true });
  }
}
